import React, { useState, useEffect } from 'react';
import { Card, Button, Spinner, Alert, Row, Col, Badge } from 'react-bootstrap';
import CarteV2Service from '../../../services/CarteV2Service';
import { stockService } from '../../services/StockService';

/**
 * Composant pour l'approvisionnement du stock de cartes
 */
const StockRestockPanel = () => {
    const [stockItems, setStockItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [selectedStock, setSelectedStock] = useState(null);
    const [formData, setFormData] = useState({
        quantity: 0,
        unitCost: 0,
        supplier: '',
        supplierInvoiceNumber: '',
        receivedDate: new Date().toISOString().split('T')[0],
        notes: '',
        // Serial range generation
        generateSerials: true,
        startSerial: '',
        endSerial: '',
        batchNumber: '',
        prefix: '',
        suffix: ''
    });
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState(null);

    useEffect(() => {
        loadStockItems();
    }, []);

    const loadStockItems = async () => {
        try {
            setLoading(true);
            const stocks = await stockService.getStockSummary();
            console.log('Loaded stocks:', stocks);
            setStockItems(Array.isArray(stocks) ? stocks : []);
        } catch (error) {
            console.error('Error loading stock:', error);
            setStockItems([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleRestock = (stock) => {
        setSelectedStock(stock);
        setFormData({
            quantity: 0,
            unitCost: stock.unitCost,
            supplier: stock.supplier,
            supplierInvoiceNumber: '',
            receivedDate: new Date().toISOString().split('T')[0],
            notes: '',
            generateSerials: true,
            startSerial: '',
            endSerial: '',
            batchNumber: `BATCH-${Date.now()}`,
            prefix: stock.countryCode || '',
            suffix: ''
        });
        setShowForm(true);
        setMessage(null);
    };

    // Auto-generate end serial when quantity or start serial changes
    useEffect(() => {
        if (formData.generateSerials && formData.startSerial && formData.quantity > 0) {
            try {
                const startNum = parseInt(formData.startSerial.replace(/\D/g, ''));
                if (!isNaN(startNum)) {
                    const endNum = startNum + formData.quantity - 1;
                    const endSerial = formData.prefix + endNum.toString().padStart(formData.startSerial.replace(/\D/g, '').length, '0') + formData.suffix;
                    setFormData(prev => ({ ...prev, endSerial }));
                }
            } catch (e) {
                console.error('Error generating end serial:', e);
            }
        }
    }, [formData.startSerial, formData.quantity, formData.generateSerials, formData.prefix, formData.suffix]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.quantity || formData.quantity <= 0) {
            setMessage({ type: 'danger', text: 'La quantité doit être supérieure à 0' });
            return;
        }
        if (!formData.unitCost || formData.unitCost <= 0) {
            setMessage({ type: 'danger', text: 'Le coût unitaire doit être supérieur à 0' });
            return;
        }
        if (!formData.supplier) {
            setMessage({ type: 'danger', text: 'Le fournisseur est obligatoire' });
            return;
        }
        if (!formData.batchNumber) {
            setMessage({ type: 'danger', text: 'Le numéro de lot est obligatoire' });
            return;
        }
        if (formData.generateSerials) {
            if (!formData.startSerial) {
                setMessage({ type: 'danger', text: 'Le numéro de série de départ est obligatoire' });
                return;
            }
        }

        try {
            setSubmitting(true);
            setMessage(null);

            // 1. Restock the item
            const result = await stockService.restockStandard(selectedStock.id, {
                quantity: formData.quantity,
                unitCost: formData.unitCost,
                supplier: formData.supplier,
                supplierInvoiceNumber: formData.supplierInvoiceNumber,
                receivedDate: formData.receivedDate,
                notes: formData.notes,
                receivedBy: 'admin@system.com'
            });

            let successMessage = result.message || `${formData.quantity} articles ajoutés au stock ${selectedStock.productCode}. Coût total: ${result.totalCost.toLocaleString('fr-FR')} FCFA`;

            // 2. Generate serial range if requested
            if (formData.generateSerials) {
                try {
                    const serialRangeResult = await CarteV2Service.createSerialRange({
                        cardStockId: selectedStock.id,
                        startSerial: formData.startSerial,
                        endSerial: formData.endSerial,
                        totalQuantity: formData.quantity,
                        batchNumber: formData.batchNumber,
                        prefix: formData.prefix,
                        suffix: formData.suffix,
                        notes: `Plage générée lors du réapprovisionnement du ${formData.receivedDate}`
                    });

                    successMessage += `\n✓ Plage de numéros de série créée: ${formData.startSerial} → ${formData.endSerial}`;
                } catch (serialError) {
                    console.error('Error creating serial range:', serialError);
                    successMessage += '\n⚠ Attention: Erreur lors de la création de la plage de numéros de série';
                }
            }

            setMessage({
                type: 'success',
                text: successMessage
            });

            // Rafraîchir les données
            loadStockItems();

            // Fermer le formulaire après 3 secondes
            setTimeout(() => {
                setShowForm(false);
                setSelectedStock(null);
            }, 3000);

        } catch (error) {
            setMessage({
                type: 'danger',
                text: 'Erreur lors du réapprovisionnement: ' + error.message
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setSelectedStock(null);
        setMessage(null);
    };

    const calculateTotal = () => {
        return (formData.quantity * formData.unitCost).toLocaleString('fr-FR');
    };

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2">Chargement des stocks...</p>
            </div>
        );
    }

    return (
        <div className="stock-restock-panel">
            <Row>
                <Col md={12}>
                    <h4 className="mb-4">
                        <i className="fa fa-box me-2"></i>
                        Approvisionnement du Stock
                    </h4>
                </Col>
            </Row>

            {/* Liste des stocks */}
            {!showForm && (
                <Row>
                    {stockItems.map(stock => (
                        <Col md={6} lg={4} key={stock.id} className="mb-3">
                            <Card className={stock.isLowStock ? 'border-warning' : ''}>
                                <Card.Header className="d-flex justify-content-between align-items-center">
                                    <span>
                                        <strong>{stock.productCode}</strong>
                                    </span>
                                    <Badge bg={stock.currentQuantity === 0 ? 'danger' : stock.isLowStock ? 'warning' : 'success'}>
                                        {stock.currentQuantity} unités
                                    </Badge>
                                </Card.Header>
                                <Card.Body>
                                    <h6>{stock.productName}</h6>
                                    <hr />
                                    <div className="mb-2">
                                        <small className="text-muted">Pays:</small> <strong>{stock.countryCode}</strong>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted">Coût unitaire:</small> <strong>{stock.unitCost.toLocaleString('fr-FR')} FCFA</strong>
                                    </div>
                                    <div className="mb-2">
                                        <small className="text-muted">Seuil minimum:</small> <strong>{stock.minimumThreshold}</strong>
                                    </div>
                                    <div className="mb-3">
                                        <small className="text-muted">Fournisseur:</small> <strong>{stock.supplier}</strong>
                                    </div>

                                    {stock.isLowStock && (
                                        <Alert variant="warning" className="mb-2 py-1 px-2">
                                            <small><i className="fa fa-exclamation-triangle me-1"></i> Stock faible</small>
                                        </Alert>
                                    )}

                                    <Button
                                        variant={stock.isLowStock ? 'warning' : 'primary'}
                                        size="sm"
                                        className="w-100"
                                        onClick={() => handleRestock(stock)}
                                    >
                                        <i className="fa fa-plus me-2"></i>
                                        Réapprovisionner
                                    </Button>
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>
            )}

            {/* Formulaire de réapprovisionnement */}
            {showForm && selectedStock && (
                <Card>
                    <Card.Header>
                        <h5 className="mb-0">
                            <i className="fa fa-plus-circle me-2"></i>
                            Réapprovisionnement - {selectedStock.productCode}
                        </h5>
                    </Card.Header>
                    <Card.Body>
                        {message && (
                            <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
                                {message.text}
                            </Alert>
                        )}

                        {/* Info stock actuel */}
                        <Alert variant="light" className="mb-3">
                            <Row>
                                <Col md={6}>
                                    <strong>{selectedStock.productName}</strong><br />
                                    <small className="text-muted">{selectedStock.productCode}</small>
                                </Col>
                                <Col md={6} className="text-end">
                                    <Badge bg={selectedStock.currentQuantity === 0 ? 'danger' : selectedStock.isLowStock ? 'warning' : 'success'}>
                                        Stock actuel: {selectedStock.currentQuantity}
                                    </Badge>
                                    <br />
                                    <small className="text-muted">Seuil: {selectedStock.minimumThreshold}</small>
                                </Col>
                            </Row>
                        </Alert>

                        <form onSubmit={handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">Quantité à ajouter *</label>
                                        <input
                                            type="number"
                                            className="form-control"
                                            value={formData.quantity}
                                            onChange={(e) => handleFieldChange('quantity', parseInt(e.target.value) || 0)}
                                            placeholder="Nombre d'articles"
                                            required
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">Coût unitaire (FCFA) *</label>
                                        <div className="input-group">
                                            <input
                                                type="number"
                                                className="form-control"
                                                value={formData.unitCost}
                                                onChange={(e) => handleFieldChange('unitCost', parseFloat(e.target.value) || 0)}
                                                placeholder="0.00"
                                                step="0.01"
                                                required
                                            />
                                            <span className="input-group-text">FCFA</span>
                                        </div>
                                    </div>
                                </Col>
                            </Row>

                            {formData.quantity > 0 && formData.unitCost > 0 && (
                                <Alert variant="info" className="mb-3">
                                    <strong>Coût total:</strong> {calculateTotal()} FCFA
                                    <br />
                                    <strong>Nouveau stock:</strong> {selectedStock.currentQuantity + formData.quantity} unités
                                </Alert>
                            )}

                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">Fournisseur *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.supplier}
                                            onChange={(e) => handleFieldChange('supplier', e.target.value)}
                                            placeholder="Nom du fournisseur"
                                            required
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">N° Facture fournisseur</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.supplierInvoiceNumber}
                                            onChange={(e) => handleFieldChange('supplierInvoiceNumber', e.target.value)}
                                            placeholder="N° de facture"
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">Date de réception</label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={formData.receivedDate}
                                            onChange={(e) => handleFieldChange('receivedDate', e.target.value)}
                                        />
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label">Numéro de lot *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.batchNumber}
                                            onChange={(e) => handleFieldChange('batchNumber', e.target.value)}
                                            placeholder="Ex: LOT-2024-001"
                                            required
                                        />
                                        <small className="text-muted">Identifiant unique pour tracer ce lot</small>
                                    </div>
                                </Col>
                            </Row>

                            <div className="mb-3">
                                <label className="form-label">Notes</label>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={formData.notes}
                                    onChange={(e) => handleFieldChange('notes', e.target.value)}
                                    placeholder="Remarques sur ce réapprovisionnement..."
                                />
                            </div>

                            {/* Serial Number Generation Section */}
                            <hr className="my-4" />
                            <div className="mb-3">
                                <div className="form-check form-switch">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="generateSerials"
                                        checked={formData.generateSerials}
                                        onChange={(e) => handleFieldChange('generateSerials', e.target.checked)}
                                    />
                                    <label className="form-check-label" htmlFor="generateSerials">
                                        <strong>Générer une plage de numéros de série</strong>
                                    </label>
                                </div>
                                <small className="text-muted">Créer automatiquement une plage de numéros pour les cartes</small>
                            </div>

                            {formData.generateSerials && (
                                <>
                                    <Alert variant="info" className="mb-3">
                                        <small><i className="fa fa-info-circle me-2"></i>La plage sera créée automatiquement après l'approvisionnement</small>
                                    </Alert>

                                    <Row>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <label className="form-label">Préfixe</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.prefix}
                                                    onChange={(e) => handleFieldChange('prefix', e.target.value)}
                                                    placeholder="Ex: CM, CI..."
                                                />
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <label className="form-label">N° de série début *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.startSerial}
                                                    onChange={(e) => handleFieldChange('startSerial', e.target.value)}
                                                    placeholder="Ex: 0000001"
                                                    required={formData.generateSerials}
                                                />
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <label className="form-label">Suffixe</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={formData.suffix}
                                                    onChange={(e) => handleFieldChange('suffix', e.target.value)}
                                                    placeholder="Ex: 2024"
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <div className="mb-3">
                                        <label className="form-label">N° de série fin (auto)</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.endSerial}
                                            readOnly
                                            placeholder="Calculé automatiquement"
                                        />
                                        <small className="text-muted">Calculé automatiquement selon la quantité</small>
                                    </div>

                                    {formData.startSerial && formData.endSerial && (
                                        <Alert variant="success" className="mb-3">
                                            <div><strong>Aperçu de la plage:</strong></div>
                                            <div className="mt-2">
                                                <Badge bg="primary" className="me-2">{formData.prefix}{formData.startSerial}{formData.suffix}</Badge>
                                                <i className="fa fa-arrow-right mx-2"></i>
                                                <Badge bg="primary">{formData.endSerial}</Badge>
                                            </div>
                                            <small className="d-block mt-2">
                                                {formData.quantity} numéros seront générés dans le lot <strong>{formData.batchNumber}</strong>
                                            </small>
                                        </Alert>
                                    )}
                                </>
                            )}

                            <div className="d-flex gap-2">
                                <Button
                                    variant="secondary"
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    Annuler
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <Spinner animation="border" size="sm" className="me-2" />
                                            Réapprovisionnement en cours...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-check me-2"></i>
                                            Réapprovisionner
                                        </>
                                    )}
                                </Button>
                            </div>
                        </form>
                    </Card.Body>
                </Card>
            )}
        </div>
    );
};

export default StockRestockPanel;
